/**
 * Providers referred on a Care Concierge case (Feature 106).
 *
 * A search over the directory first, with a typed name as the fallback. The column has held
 * `service_provider_id` since the feature's first migration — it was free text only because
 * requirement v4 item 13 deferred anything that acts on a referral, so nothing yet needed the link.
 * Searching is what makes the link the default instead of the exception, and a referral to someone
 * who is not on bQuest is still a real referral, so typing a name stays available.
 *
 * Adapted from AuthorProSelector, which does the same job for resource authors. Not shared with it:
 * that one searches professionals by user id and cannot express an entry that is only a name.
 */

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Loader2, Plus, Search, X } from 'lucide-react';
import { Label } from '@/shared/components/ui/label';
import { useProviderDirectorySearch } from '../../hooks/useProviderDirectorySearch';
import type { ReferredProvider } from '../../services/careConciergeCasesApi';

type ReferredProvidersFieldProps = {
  value: ReferredProvider[];
  onChange: (next: ReferredProvider[]) => void;
  disabled?: boolean;
};

export const ReferredProvidersField = ({
  value,
  onChange,
  disabled,
}: ReferredProvidersFieldProps) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  /** Which suggestion the arrow keys are on. -1 means none, and Enter then takes the typed name. */
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    data: matches = [],
    isFetching,
    isError,
    isSuccess,
    isSearchable,
    isDebouncing,
    refetch,
  } = useProviderDirectorySearch(search);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const alreadyPicked = new Set(
    value.map((entry) => entry.serviceProviderId).filter((id): id is string => id !== null),
  );
  const available = matches.filter((match) => !alreadyPicked.has(match.serviceProviderId));

  const add = (entry: ReferredProvider) => {
    onChange([...value, entry]);
    setSearch('');
    setIsOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const remove = (index: number) => {
    onChange(value.filter((_, position) => position !== index));
  };

  const typed = search.trim();

  /**
   * The directory has been consulted for exactly what is typed right now — or the term is too
   * short to consult it with, which no real company name is.
   *
   * Every state that is not this one looks identical from the outside: mid-debounce, in flight and
   * failed all present as "no matches", and each would let someone save a provider who does have a
   * bQuest profile as a typed-in name, with no id and nothing on the record to say a lookup never
   * happened. isSuccess is the only positive evidence that one did.
   */
  const directoryAnswered =
    !isDebouncing && !isFetching && !isError && (isSuccess || !isSearchable);

  /**
   * Offered even when the directory has matches: a provider can share a name with one on bQuest,
   * and the team is the one who knows which they meant. Withheld until the directory has answered,
   * for the reason above — the retry in the dropdown is the way out of a failed one.
   */
  const canAddTyped =
    typed.length > 0 &&
    !value.some((entry) => entry.name.toLowerCase() === typed.toLowerCase()) &&
    directoryAnswered;

  // Closed outright while saving rather than disabling each option in it: the dropdown is the one
  // part of this field that adds to the list, and leaving it open with the input greyed out lets
  // an admin keep picking providers after the payload has already gone to the server.
  // isDebouncing keeps it open through the pause after a keystroke. Without it the list would
  // vanish and come back on every burst of typing, and the field would look like it had stopped
  // responding in exactly the window where it is busiest.
  const showDropdown =
    !disabled && isOpen && (isSearchable || isDebouncing || canAddTyped || isError);

  /**
   * Arrow keys walk the directory matches only, and Enter with nothing highlighted takes the typed
   * name instead — which is what someone who has just typed a name nobody matched expects to
   * happen. That keeps the "add as a name" row out of the listbox, where being reachable by arrow
   * would make it read as one of the directory's own suggestions.
   *
   * Follows LocationSearch, the one place in the app that already implements combobox keys. Like
   * it, the highlighted row is marked with aria-selected rather than aria-activedescendant, which
   * nothing in this codebase uses.
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (!showDropdown) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((previous) =>
          previous < available.length - 1 ? previous + 1 : previous,
        );
        break;

      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((previous) => (previous > 0 ? previous - 1 : -1));
        break;

      case 'Enter': {
        event.preventDefault();
        // Read through `available` rather than trusting the index: the list reshapes as results
        // arrive, and a stale index would otherwise reach past the end.
        const highlighted = activeIndex >= 0 ? available[activeIndex] : undefined;

        if (highlighted) add(highlighted);
        else if (canAddTyped) add({ serviceProviderId: null, name: typed });
        break;
      }

      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <Label htmlFor="cc-case-providers">Providers referred</Label>

      {value.length > 0 && (
        <ul className="mt-1 space-y-1">
          {value.map((entry, index) => (
            <li
              key={`${entry.serviceProviderId ?? 'typed'}-${entry.name}-${index}`}
              className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-1.5 text-sm"
            >
              <span className="min-w-0 truncate">
                {entry.name}
                {/* Says which entries the platform can act on later, and which are just a note to
                    the next person reading the case. */}
                {entry.serviceProviderId === null && (
                  <span className="ml-2 text-xs text-muted-foreground">not on bQuest</span>
                )}
              </span>
              <button
                type="button"
                aria-label={`Remove ${entry.name}`}
                onClick={() => remove(index)}
                disabled={disabled}
                className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="relative mt-2">
        <Search
          className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          id="cc-case-providers"
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls="cc-case-providers-results"
          aria-autocomplete="list"
          autoComplete="off"
          value={search}
          placeholder="Search bQuest providers, or type a name"
          disabled={disabled}
          onChange={(event) => {
            setSearch(event.target.value);
            setIsOpen(true);
            // A new term means new results; keeping the old position would highlight whichever
            // provider happens to land in that slot.
            setActiveIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm disabled:opacity-50"
        />
      </div>

      {showDropdown && (
        <div
          id="cc-case-providers-results"
          role="listbox"
          aria-label="bQuest provider suggestions"
          className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-background shadow-lg"
        >
          {/* The pause before the request counts as searching to everyone but the code. */}
          {(isFetching || isDebouncing) && (
            <p className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              Searching…
            </p>
          )}

          {/* Said out loud rather than left to read as an empty result, which is what it would
              otherwise look like — and an empty result here invites exactly the wrong action. */}
          {isError && (
            <div className="p-3">
              <p className="text-sm text-destructive">Could not search bQuest providers.</p>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => void refetch()}
                className="mt-1 text-sm underline underline-offset-4"
              >
                Retry
              </button>
            </div>
          )}

          {available.map((match, index) => (
            <button
              key={match.serviceProviderId}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              // Keeps the click from blurring the input first, which would close this list before
              // the selection lands.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => add(match)}
              onMouseEnter={() => setActiveIndex(index)}
              className={`block w-full truncate px-3 py-2 text-left text-sm hover:bg-muted ${
                index === activeIndex ? 'bg-muted' : ''
              }`}
            >
              {match.name}
            </button>
          ))}

          {/* Not an option: this adds something the directory does not contain, so announcing it
              as one of the suggestions would misdescribe it. Same shape as the "not listed"
              escape hatch in PartnerCompanyTypeahead. */}
          {canAddTyped && (
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => add({ serviceProviderId: null, name: typed })}
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm hover:bg-muted"
            >
              <Plus className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">Add “{typed}” as a name</span>
            </button>
          )}

          {!isFetching && !isDebouncing && !isError && available.length === 0 && !canAddTyped && (
            <p className="p-3 text-sm text-muted-foreground">
              {isSearchable ? 'No providers match, and that name is already on the case.' : null}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
